import * as AuthActions from '@/store/actions/auth';
import { STORAGE } from '@/constants/storage.constants';

import Api from '@/services/api';
import AuthService from '@/services/auth.service';
import * as jwtDecode from 'jwt-decode';

const getUserData = (token) => {
  const {
    name,
    email,
    picture,
    network_id,
    network_name,
    points,
    ranking,
  } = jwtDecode(token);

  return {
    name,
    email,
    picture,
    network_id,
    network_name,
    points,
    ranking,
  };
};

const state = {
  loading: true,
  error: false,
  token: null,
  user: {
    name: null,
    email: null,
    picture: null,
    network_id: null,
    network_name: null,
    points: null,
    ranking: {
      unit_ranking: 0,
      resale_ranking: 0,
    },
  },
  firstAccess: false,
  firstAccessCpf: null,
};

const getters = {
  isAuthenticated: (state) => !!state.token,
  user: (state) => state.user,
  userPoints: (state) => state.user.points || 0,
};

const actions = {
  [AuthActions.AUTH_TOKEN]: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit(AuthActions.AUTH_TOKEN);

      const token = localStorage.getItem(STORAGE.AUTH_TOKEN);
      Api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      if (!token) {
        commit(AuthActions.AUTH_TOKEN_ERROR);
        reject('No token.');
        return;
      }

      AuthService.refresh()
        .then(({ data }) => {
          const { token: newToken } = data;

          commit(AuthActions.AUTH_TOKEN_SUCCESS, newToken);
          localStorage.setItem(STORAGE.AUTH_TOKEN, newToken);
          resolve(token);
        })
        .catch(() => {
          commit(AuthActions.AUTH_TOKEN_ERROR);
          localStorage.removeItem(STORAGE.AUTH_TOKEN);
          reject('Token expired.');
        });
    });
  },
  [AuthActions.AUTH_REQUEST]: ({ commit }, user) => {
    return new Promise((resolve, reject) => {
      commit(AuthActions.AUTH_REQUEST);

      AuthService.login(user)
        .then(({ data }) => {
          const { token } = data;

          localStorage.setItem(STORAGE.AUTH_TOKEN, token);
          commit(AuthActions.AUTH_REQUEST_SUCCESS, token);
          resolve(data);
        })
        .catch((err) => {
          commit(AuthActions.AUTH_REQUEST_ERROR, err);
          localStorage.removeItem(STORAGE.AUTH_TOKEN);
          reject(err);
        });
    });
  },
  [AuthActions.AUTH_LOGOUT]: ({ commit }) => {
    return new Promise((resolve) => {
      commit(AuthActions.AUTH_LOGOUT);
      localStorage.removeItem(STORAGE.AUTH_TOKEN);
      resolve();
    });
  },
  [AuthActions.SUM_POINTS]: ({ commit }, points) =>
    commit(AuthActions.SUM_POINTS, points),
  [AuthActions.UPDATE_USER]: ({ commit }, user) =>
    commit(AuthActions.UPDATE_USER, user),
  [AuthActions.AUTH_FIRST]: ({ commit }, cpf) => {
    return new Promise((resolve, reject) => {
      commit(AuthActions.AUTH_FIRST);

      return AuthService.firstAccess(cpf)
        .then(() => {
          commit(AuthActions.AUTH_FIRST_SUCCESS, cpf);
          resolve(cpf);
        })
        .catch((error) => {
          commit(AuthActions.AUTH_TOKEN_ERROR);
          reject(error);
        });
    });
  },
  [AuthActions.CANCEL_FIRST]: ({ commit }) => commit(AuthActions.CANCEL_FIRST),
  [AuthActions.FINISH_FIRST]: ({ commit }, client) => {
    return new Promise((resolve, reject) => {
      AuthService.firstAccessLogin(client)
        .then(({ data }) => {
          const { token } = data;

          localStorage.setItem(STORAGE.AUTH_TOKEN, token);
          commit(AuthActions.FINISH_FIRST_SUCCESS, token);
          resolve(token);
        })
        .catch((error) => reject(error));
    });
  },
};

const mutations = {
  [AuthActions.AUTH_TOKEN]: (state) => {
    state.loading = true;
    state.error = false;
  },
  [AuthActions.AUTH_TOKEN_SUCCESS]: (state, token) => {
    Api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    state.token = token;
    state.loading = false;
    state.user = getUserData(token);
  },
  [AuthActions.AUTH_TOKEN_ERROR]: (state) => {
    state.loading = false;
    state.user = {};
    state.token = null;

    Api.defaults.headers.common['Authorization'] = null;
  },
  [AuthActions.AUTH_REQUEST]: (state) => {
    state.error = false;
  },
  [AuthActions.AUTH_REQUEST_SUCCESS]: (state, token) => {
    Api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    state.token = token;
    state.user = getUserData(token);
  },
  [AuthActions.AUTH_REQUEST_ERROR]: (state) => {
    state.error = true;
    state.user = {};
    state.token = null;
  },
  [AuthActions.AUTH_LOGOUT]: (state) => {
    state.token = null;
    state.user = {};
  },
  [AuthActions.SUM_POINTS]: (state, points) => {
    const sum = parseFloat((state.user.points + points).toFixed(2));
    state.user.points = sum;
  },
  [AuthActions.UPDATE_USER]: (state, user) => {
    state.user = {
      ...state.user,
      ...user,
    };
  },
  [AuthActions.AUTH_FIRST]: (state) => {
    state.firstAccess = false;
    state.firstAccessCpf = null;
  },
  [AuthActions.CANCEL_FIRST]: (state) => {
    state.firstAccess = false;
    state.firstAccessCpf = null;
  },
  [AuthActions.AUTH_FIRST_SUCCESS]: (state, cpf) => {
    state.firstAccess = true;
    state.firstAccessCpf = cpf;
  },
  [AuthActions.AUTH_FIRST_ERROR]: (state) => {
    state.firstAccess = false;
    state.firstAccessCpf = null;
  },
  [AuthActions.FINISH_FIRST_SUCCESS]: (state, token) => {
    Api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    state.token = token;
    state.user = getUserData(token);

    state.firstAccess = false;
    state.firstAccessCpf = null;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
